/**
 * Simple localStorage caching utility for API responses
 * Provides a low-maintenance caching strategy with TTL support
 */

const CACHE_PREFIX = 'mars_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default TTL

/**
 * Cache configuration for different data types
 * TTL values in milliseconds
 */
const CACHE_CONFIG = {
  home: { ttl: 5 * 60 * 1000 },      // 5 minutes - home page data changes occasionally
  games: { ttl: 10 * 60 * 1000 },    // 10 minutes - game lists
  game: { ttl: 30 * 60 * 1000 },     // 30 minutes - individual game data
  categories: { ttl: 60 * 60 * 1000 }, // 1 hour - categories rarely change
  leaderboard: { ttl: 60 * 1000 },   // 1 minute - leaderboard updates frequently
  config: { ttl: 24 * 60 * 60 * 1000 }, // 24 hours - config rarely changes
};

/**
 * Get data from cache
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null if expired/not found
 */
export function getCache(key) {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const { data, timestamp, ttl } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - timestamp < ttl) {
      return data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.warn('Cache read error:', error);
    return null;
  }
}

/**
 * Set data in cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {string} type - Cache type from CACHE_CONFIG (optional)
 */
export function setCache(key, data, type = null) {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const ttl = type && CACHE_CONFIG[type] ? CACHE_CONFIG[type].ttl : DEFAULT_TTL;
    
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    // localStorage might be full or disabled
    console.warn('Cache write error:', error);
    // Try to clear old cache entries
    clearExpiredCache();
  }
}

/**
 * Remove specific cache entry
 * @param {string} key - Cache key
 */
export function removeCache(key) {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch (error) {
    console.warn('Cache remove error:', error);
  }
}

/**
 * Clear all expired cache entries
 */
export function clearExpiredCache() {
  try {
    const now = Date.now();
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (cached && now - cached.timestamp >= cached.ttl) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // Invalid cache entry, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Cache cleanup error:', error);
  }
}

/**
 * Clear all cache entries
 */
export function clearAllCache() {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Cache clear error:', error);
  }
}

/**
 * Check if cache exists and is valid (without returning data)
 * @param {string} key - Cache key
 * @returns {boolean}
 */
export function hasValidCache(key) {
  return getCache(key) !== null;
}

export default {
  get: getCache,
  set: setCache,
  remove: removeCache,
  clearExpired: clearExpiredCache,
  clearAll: clearAllCache,
  hasValid: hasValidCache,
  CACHE_CONFIG
};
